import type { Content } from 'pdfmake/interfaces.js';
import { formatDay } from '../../../format.js';
import {
  pdfBlock,
  pdfFacts,
  pdfHeading,
  pdfTable,
  pdfText,
  type RecordPdfApproval,
  type RecordPdfT,
  recordPdf,
} from '../../reports/record-pdf.js';
import type { ReportBrand } from '../../voyage/source.js';
import type { CompanyNcDetail } from './types.js';

/**
 * Şirket uygunsuzluğunun PDF'i (ADR 0062 §3): genel bilgiler, açıklama, kök neden, faaliyetler,
 * doğrulama ve onay izi (açan; kapanış ve yeniden açma: ad, ofis rolü, zaman). Ofis kaydında gemi
 * saati yoktur: zamanlar UTC.
 */
export function companyNcPdf(
  t: RecordPdfT,
  input: { detail: CompanyNcDetail; brand: ReportBrand | null; printedAt: string },
) {
  const { detail } = input;
  const c = (key: string, options?: Record<string, unknown>) => t(`companyNc.${key}`, options);
  const name = (id: string | null) => (id ? (detail.names[id] ?? id) : null);
  const roles = (ids: readonly string[]) =>
    ids
      .map((id) => detail.roles[id])
      .filter(Boolean)
      .join(', ') || null;
  const content: Content[] = [
    pdfFacts([
      [c('fields.source'), t(`companyNc.sources.${detail.source}`)],
      [c('fields.ncClass'), t(`hse.ncClasses.${detail.ncClass}`)],
      [c('fields.detectedOn'), formatDay(detail.detectedOn)],
      [c('fields.detectedBy'), detail.detectedBy],
      [c('fields.vessel'), detail.vesselName ?? c('noVessel')],
      [
        c('fields.department'),
        detail.department ? t(`companyNc.departments.${detail.department}`) : '—',
      ],
      [c('fields.smsReference'), pdfText(detail.smsReference)],
      [c('fields.status'), t(`companyNc.statuses.${detail.status}`)],
      [c('fields.responsible'), pdfText(detail.responsible)],
      [c('fields.targetDate'), detail.targetDate ? formatDay(detail.targetDate) : '—'],
    ]),
    ...pdfBlock(c('fields.description'), detail.description),
    ...pdfBlock(c('fields.immediateAction'), detail.immediateAction),
    ...pdfBlock(c('fields.rootCause'), detail.rootCause),
  ];
  if (detail.actions.length > 0) {
    content.push(
      pdfHeading(c('actions.title')),
      pdfTable(
        [
          '#',
          c('actions.kind'),
          c('actions.description'),
          c('actions.responsible'),
          c('actions.due'),
          c('actions.status'),
        ],
        detail.actions.map((a) => [
          String(a.actionNo),
          t(`hse.actionKinds.${a.kind}`),
          [
            a.description,
            a.completionNote ? `${c('actions.completion')}: ${a.completionNote}` : null,
            a.cancelReason ? `${c('actions.cancelled')}: ${a.cancelReason}` : null,
          ]
            .filter(Boolean)
            .join('\n'),
          a.responsible,
          formatDay(a.dueDate),
          t(`hse.actionStatuses.${a.status}`),
        ]),
        [16, 52, '*', 70, 52, 52],
      ),
    );
  }
  content.push(...pdfBlock(c('fields.verificationNote'), detail.verificationNote));

  const approvals: RecordPdfApproval[] = [
    {
      step: c('pdf.opened'),
      name: name(detail.createdBy),
      role: null,
      at: detail.createdAt,
      offsetMin: null,
    },
    ...[...detail.events].reverse().map((e) => ({
      step: c(`events.${e.kind}`),
      name: name(e.by),
      role: roles(e.roleIds),
      at: e.at,
      offsetMin: null,
      note: e.note,
    })),
  ];
  const marks: string[] = [];
  if (detail.status === 'closed') marks.push(t('recordPdf.closedMark'));
  if (detail.status === 'reopened') marks.push(t('recordPdf.reopenedMark'));
  return recordPdf(t, {
    brand: input.brand,
    title: `${c('pdf.title')} — ${detail.title}`,
    number: detail.ncNo,
    marks,
    subject: input.brand?.name ?? detail.ncNo,
    content,
    approvals,
    printedAt: input.printedAt,
  });
}
